import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import storage from '@/services/storage'
import { defaultCategories, categoryGroups } from '@/data/categories'
import { defaultBookmarks } from '@/data/bookmarks'

export const useNavStore = defineStore('nav', () => {
  // 状态
  const categories = ref([])
  const bookmarks = ref([])
  const loading = ref(false)
  const searchKeyword = ref('')
  const activeCategory = ref(null)
  const sidebarOpen = ref(false)
  const theme = ref('dark')

  // 计算属性
  const groupedCategories = computed(() => {
    return categoryGroups.map(group => ({
      ...group,
      items: categories.value.filter(cat => group.categories.includes(cat.id))
    }))
  })

  const filteredBookmarks = computed(() => {
    let result = bookmarks.value

    if (activeCategory.value) {
      result = result.filter(b => b.categoryId === activeCategory.value)
    }

    if (searchKeyword.value) {
      const keyword = searchKeyword.value.toLowerCase()
      result = result.filter(b =>
        b.name.toLowerCase().includes(keyword) ||
        b.url.toLowerCase().includes(keyword) ||
        (b.description && b.description.toLowerCase().includes(keyword)) ||
        (b.tags && b.tags.some(t => t.toLowerCase().includes(keyword)))
      )
    }

    return result
  })

  const bookmarksByCategory = computed(() => {
    const grouped = {}
    for (const bookmark of bookmarks.value) {
      if (!grouped[bookmark.categoryId]) {
        grouped[bookmark.categoryId] = []
      }
      grouped[bookmark.categoryId].push(bookmark)
    }
    return grouped
  })

  const recentBookmarks = computed(() => {
    return [...bookmarks.value]
      .filter(b => b.lastVisit)
      .sort((a, b) => (b.lastVisit || 0) - (a.lastVisit || 0))
      .slice(0, 10)
  })

  const frequentBookmarks = computed(() => {
    return [...bookmarks.value]
      .sort((a, b) => (b.visitCount || 0) - (a.visitCount || 0))
      .slice(0, 10)
  })

  // 方法
  async function init() {
    loading.value = true
    try {
      await storage.initDefaultData(defaultCategories, defaultBookmarks)
      categories.value = await storage.getCategories()
      bookmarks.value = await storage.getBookmarks()
      
      const savedTheme = await storage.getSetting('theme', 'dark')
      setTheme(savedTheme)
    } catch (error) {
      console.error('初始化数据失败:', error)
    } finally {
      loading.value = false
    }
  }

  function setTheme(newTheme) {
    theme.value = newTheme
    document.documentElement.setAttribute('data-theme', newTheme)
    storage.setSetting('theme', newTheme)
  }

  function toggleTheme() {
    setTheme(theme.value === 'dark' ? 'light' : 'dark')
  }

  function setActiveCategory(categoryId) {
    activeCategory.value = categoryId
  }

  function setSearchKeyword(keyword) {
    searchKeyword.value = keyword
  }

  function toggleSidebar() {
    sidebarOpen.value = !sidebarOpen.value
  }

  async function addBookmark(bookmark) {
    const id = await storage.addBookmark(bookmark)
    bookmarks.value.push({ ...bookmark, id })
    return id
  }

  async function updateBookmark(id, updates) {
    await storage.updateBookmark(id, updates)
    const index = bookmarks.value.findIndex(b => b.id === id)
    if (index !== -1) {
      bookmarks.value[index] = { ...bookmarks.value[index], ...updates }
    }
  }

  async function deleteBookmark(id) {
    await storage.deleteBookmark(id)
    bookmarks.value = bookmarks.value.filter(b => b.id !== id)
  }

  async function visitBookmark(id) {
    await storage.incrementVisit(id)
    const bookmark = bookmarks.value.find(b => b.id === id)
    if (bookmark) {
      bookmark.visitCount = (bookmark.visitCount || 0) + 1
      bookmark.lastVisit = Date.now()
    }
  }

  async function addCategory(category) {
    const id = await storage.addCategory(category)
    categories.value.push({ ...category, id })
    return id
  }

  async function updateCategory(id, updates) {
    await storage.updateCategory(id, updates)
    const index = categories.value.findIndex(c => c.id === id)
    if (index !== -1) {
      categories.value[index] = { ...categories.value[index], ...updates }
    }
  }

  async function deleteCategory(id) {
    await storage.deleteCategory(id)
    categories.value = categories.value.filter(c => c.id !== id)
    bookmarks.value = bookmarks.value.filter(b => b.categoryId !== id)
  }

  async function exportData() {
    return await storage.exportData()
  }

  async function importData(data) {
    await storage.importData(data)
    categories.value = await storage.getCategories()
    bookmarks.value = await storage.getBookmarks()
  }

  return {
    // 状态
    categories,
    bookmarks,
    loading,
    searchKeyword,
    activeCategory,
    sidebarOpen,
    theme,
    // 计算属性
    groupedCategories,
    filteredBookmarks,
    bookmarksByCategory,
    recentBookmarks,
    frequentBookmarks,
    // 方法
    init,
    setTheme,
    toggleTheme,
    setActiveCategory,
    setSearchKeyword,
    toggleSidebar,
    addBookmark,
    updateBookmark,
    deleteBookmark,
    visitBookmark,
    addCategory,
    updateCategory,
    deleteCategory,
    exportData,
    importData
  }
})
